import { ICartEntry } from 'features/cart/types';
import { calculateEntryTotalPrice } from 'features/cart/utils/calculate-entry-total-price';
import type { IPaymentMethod } from 'features/payment/types';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { CartPaymentType } from 'generated/graphql-gateway';
import priceForCartEntry from 'utils/menu/price-for-cart-entry';

import { DIAGNOSTIC_ROUTE_PARAM_ALLOWLIST } from './allowlists';
import {
  DiagnosticCartEntry,
  DiagnosticCheckoutPaymentContext,
  DiagnosticMoneySource,
  DiagnosticMoneyValue,
  DiagnosticPaymentMethodCategory,
  DiagnosticPricingInput,
  DiagnosticUserContext,
} from './types';

const projectPaymentMethodCategory = (
  method: IPaymentMethod | null | undefined
): DiagnosticPaymentMethodCategory | undefined => {
  if (!method) {
    return undefined;
  }
  const identifier = method.accountIdentifier || method.fdAccountId;
  if (identifier === CartPaymentType.APPLE_PAY) {
    return 'apple_pay';
  }
  if (identifier === CartPaymentType.GOOGLE_PAY) {
    return 'google_pay';
  }
  if (method.prepaid) {
    return 'gift_card';
  }
  if (method.paypal || identifier === CartPaymentType.PAYPAL) {
    return 'paypal';
  }
  if (method.venmo || identifier === CartPaymentType.VENMO) {
    return 'venmo';
  }
  if (method.cashapp || identifier === CartPaymentType.CASHAPP) {
    return 'cash_app';
  }
  if (method.cash || identifier === CartPaymentType.CASH) {
    return 'cash';
  }
  return method.credit ? 'credit_card' : undefined;
};

export const projectDiagnosticCheckoutPayment = ({
  paymentMethods,
  selectedPaymentMethod,
  loading,
  hasError,
  noPaymentMethodsAvailable,
  selectorVisible,
  canUseApplePay,
  canUseGooglePay,
}: {
  paymentMethods: IPaymentMethod[];
  selectedPaymentMethod?: IPaymentMethod | null;
  loading: boolean;
  hasError: boolean;
  noPaymentMethodsAvailable: boolean;
  selectorVisible: boolean;
  canUseApplePay: boolean;
  canUseGooglePay: boolean;
}): DiagnosticCheckoutPaymentContext => {
  const availableMethods = paymentMethods
    .map(projectPaymentMethodCategory)
    .filter((method): method is DiagnosticPaymentMethodCategory => Boolean(method))
    .filter((method, index, methods) => methods.indexOf(method) === index);

  return {
    state: hasError
      ? 'error'
      : loading
      ? 'loading'
      : noPaymentMethodsAvailable
      ? 'unavailable'
      : 'ready',
    selectorVisible,
    availableMethods: availableMethods.length ? availableMethods : undefined,
    selectedMethod: projectPaymentMethodCategory(selectedPaymentMethod),
    applePayAvailable: canUseApplePay,
    googlePayAvailable: canUseGooglePay,
  };
};

export {
  DIAGNOSTIC_PRICING_CORRELATION_ALLOWLIST,
  DIAGNOSTIC_ROUTE_PARAM_ALLOWLIST,
  DIAGNOSTIC_VARIANT_ALLOWLIST,
} from './allowlists';

export const compactDiagnosticVariants = (
  variants: Record<string, unknown>
): Record<string, string | boolean> =>
  Object.fromEntries(
    Object.entries(variants).filter(
      (entry): entry is [string, string | boolean] =>
        typeof entry[1] === 'string' || typeof entry[1] === 'boolean'
    )
  );

export const projectDiagnosticRouteParams = (
  params: Record<string, string | string[] | undefined>
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(params).flatMap(([key, value]) => {
      if (!DIAGNOSTIC_ROUTE_PARAM_ALLOWLIST.has(key)) {
        return [];
      }
      const normalized = Array.isArray(value) ? value[0] : value;
      return typeof normalized === 'string' && normalized.trim()
        ? [[key, normalized.trim().slice(0, 240)]]
        : [];
    })
  );

export const diagnosticMoney = ({
  minorAmount,
  currency,
  formatted,
  source,
  availability,
}: {
  minorAmount?: number | null;
  currency?: string | null;
  formatted?: string | null;
  source: DiagnosticMoneySource;
  availability?: DiagnosticMoneyValue['availability'];
}): DiagnosticMoneyValue => ({
  ...(typeof minorAmount === 'number' && Number.isFinite(minorAmount) ? { minorAmount } : {}),
  ...(currency ? { currency } : {}),
  ...(formatted ? { formatted } : {}),
  source,
  ...(availability ? { availability } : {}),
});

type CurrencyFormatter = (minorAmount: number) => string;

const readPlu = (
  value: unknown,
  depth = 0,
  visited: WeakSet<object> = new WeakSet()
): string | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }
  if (depth >= 4 || visited.has(value)) {
    return undefined;
  }
  visited.add(value);
  const source = value as Record<string, unknown>;
  for (const key of ['plu', 'constantPlu', 'externalReferencesId']) {
    if (typeof source[key] === 'string' && source[key]) {
      return source[key] as string;
    }
  }
  for (const nested of Object.values(source)) {
    const candidate = readPlu(nested, depth + 1, visited);
    if (candidate) {
      return candidate;
    }
  }
  return undefined;
};

export const projectLegacyCartEntry = (
  entry: ILegacyCartEntry,
  formatCurrency: CurrencyFormatter,
  currency: string
): DiagnosticCartEntry => {
  const unitPrice = entry.price;
  const lineTotal = priceForCartEntry(entry);
  return {
    type: String(entry.type ?? ''),
    name: entry.name,
    id: entry._id,
    sanityId: entry.sanityId,
    cartId: entry.cartId,
    plu: readPlu(entry.posIdsConfiguration ?? entry.vendorConfigs),
    quantity: entry.quantity,
    ...(typeof unitPrice === 'number'
      ? {
          unitPrice: diagnosticMoney({
            minorAmount: unitPrice,
            currency,
            formatted: formatCurrency(unitPrice),
            source: 'local_cart',
            availability: 'available',
          }),
        }
      : {}),
    lineTotal: diagnosticMoney({
      minorAmount: lineTotal,
      currency,
      formatted: formatCurrency(lineTotal),
      source: 'local_cart',
      availability: 'available',
    }),
    contributions: (entry.children ?? []).map(child => ({
      kind: 'child',
      name: child.name,
      id: child._id,
      sanityId: child.sanityId,
      plu: readPlu(child.posIdsConfiguration ?? child.vendorConfigs),
      quantity: child.quantity,
      price:
        typeof child.price === 'number'
          ? diagnosticMoney({
              minorAmount: child.price * (child.quantity ?? 1),
              currency,
              formatted: formatCurrency(child.price * (child.quantity ?? 1)),
              source: 'local_cart',
              availability: 'available',
            })
          : undefined,
    })),
    children: (entry.children ?? []).map(child =>
      projectLegacyCartEntry(child, formatCurrency, currency)
    ),
  };
};

export const projectMinimalCartEntry = (
  entry: ICartEntry,
  formatCurrency: CurrencyFormatter
): DiagnosticCartEntry => {
  const amount = entry.price?.amount;
  const currency = entry.price?.currency;
  const children = entry.children ?? [];
  const lineTotal = calculateEntryTotalPrice(entry);
  return {
    type: entry.type,
    name: entry.name,
    id: entry.id,
    sanityId: entry.id,
    cartId: entry.cartId,
    plu: entry.referenceId ?? undefined,
    quantity: entry.quantity,
    rewardId: entry.rewardId,
    rewardBenefitId: entry.rewardBenefitId,
    ...(typeof amount === 'number'
      ? {
          unitPrice: diagnosticMoney({
            minorAmount: amount,
            currency,
            formatted: formatCurrency(amount),
            source: 'local_cart',
            availability: 'available',
          }),
          lineTotal: diagnosticMoney({
            minorAmount: lineTotal,
            currency,
            formatted: formatCurrency(lineTotal),
            source: 'local_cart',
            availability: 'available',
          }),
        }
      : {}),
    contributions: children.map(child => ({
      kind: child.type === 'modifier' ? 'modifier' : 'child',
      name: child.name,
      id: child.id,
      plu: child.referenceId ?? undefined,
      quantity: child.quantity,
      price:
        typeof child.price?.amount === 'number'
          ? diagnosticMoney({
              minorAmount: child.price.amount * (child.quantity ?? 1),
              currency: child.price.currency,
              formatted: formatCurrency(child.price.amount * (child.quantity ?? 1)),
              source: 'local_cart',
              availability: 'available',
            })
          : undefined,
    })),
    children: children.map(child => projectMinimalCartEntry(child, formatCurrency)),
  };
};

export const projectPricingInput = ({
  storeNumber,
  serviceMode,
  cartEntryCount,
  requestedTotal,
  checkoutRepriceRequestId,
  requestId,
  correlationId,
}: DiagnosticPricingInput): DiagnosticPricingInput => ({
  storeNumber,
  serviceMode,
  cartEntryCount,
  requestedTotal,
  checkoutRepriceRequestId,
  requestId,
  correlationId,
});

export const projectDiagnosticError = (error: unknown): { code?: string; message?: string } => {
  const source = error && typeof error === 'object' ? (error as { code?: unknown }) : undefined;
  const code = typeof source?.code === 'string' ? source.code : undefined;
  return {
    ...(code ? { code } : {}),
    ...(error
      ? { message: code ? `Pricing operation failed (${code}).` : 'Pricing operation failed.' }
      : {}),
  };
};

interface LiveDiagnosticUser {
  cognitoId?: string | null;
  thLegacyCognitoId?: string | null;
  loyaltyId?: string | null;
  details?: { name?: string | null; email?: string | null } | null;
}

export const getDiagnosticPrincipalKey = ({
  isAuthenticated,
  user,
}: {
  isAuthenticated: boolean;
  user?: LiveDiagnosticUser | null;
}): string =>
  isAuthenticated
    ? user?.cognitoId
      ? `user:${user.cognitoId}`
      : 'authenticated:profile-loading'
    : 'anonymous';

export const projectDiagnosticUser = ({
  isAuthenticated,
  authLoading,
  user,
  loyaltyProfile,
}: {
  isAuthenticated: boolean;
  authLoading: boolean;
  user?: LiveDiagnosticUser | null;
  loyaltyProfile?: { id?: string | null } | null;
}): DiagnosticUserContext => {
  if (!isAuthenticated) {
    return { authenticationState: 'anonymous' };
  }
  if (authLoading || !user) {
    return { authenticationState: 'authenticated_profile_loading' };
  }
  const loyaltyProfileId = loyaltyProfile?.id ?? undefined;
  return {
    authenticationState: 'authenticated',
    name: user.details?.name || undefined,
    email: user.details?.email || undefined,
    cognitoId: user.cognitoId || undefined,
    legacyCognitoId: user.thLegacyCognitoId || undefined,
    loyaltyId: user.loyaltyId || undefined,
    loyaltyProfileLoaded: Boolean(loyaltyProfile),
    loyaltyProfileId,
    identitiesCorrespond:
      user.loyaltyId && loyaltyProfileId ? user.loyaltyId === loyaltyProfileId : undefined,
  };
};

export const projectPublicRestaurantAddress = (
  physicalAddress?: {
    address1?: string | null;
    address2?: string | null;
    city?: string | null;
    stateProvince?: string | null;
    postalCode?: string | null;
    country?: string | null;
  } | null
): string | undefined => {
  if (!physicalAddress) {
    return undefined;
  }
  return [
    physicalAddress.address1,
    physicalAddress.address2,
    [physicalAddress.city, physicalAddress.stateProvince, physicalAddress.postalCode]
      .filter(Boolean)
      .join(' '),
    physicalAddress.country,
  ]
    .filter(Boolean)
    .join(', ');
};
